import React, { useContext } from 'react';
import {
  Container, Typography, Box, Button, IconButton,
  Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { TutorialContext } from '../components/TutorialProvider';

const steps = [
  {
    label: 'Add Your Stores',
    icon: <StoreIcon />,
    description: 'Start by adding the stores and supermarkets you shop at. You can search for stores via Google Places to auto-fill their details, or add them manually with a name and address.',
  },
  {
    label: 'Add Your Groceries',
    icon: <ShoppingCartIcon />,
    description: 'Build your grocery database by scanning barcodes to automatically look up products, or enter items manually. Each grocery tracks its name, brand, category, and more.',
  },
  {
    label: 'Record Prices',
    icon: <AttachMoneyIcon />,
    description: 'Track prices for your groceries at different stores. Over time, you\'ll build a price history that helps you find the best deals and see price trends.',
  },
  {
    label: 'Create Shopping Lists',
    icon: <ListAltIcon />,
    description: 'Create shopping lists and add groceries to them. The app can analyze your lists to find the cheapest store, or show how to split your shopping across stores to save the most.',
  },
  {
    label: 'Go Shopping',
    icon: <ShoppingBagIcon />,
    description: 'When you\'re ready to shop, use the Going Shopping mode. Select a store and a list, then check off items as you go. Scan price tags to update prices in real-time.',
  },
];

const GettingStarted: React.FC = () => {
  const navigate = useNavigate();
  const tutorialContext = useContext(TutorialContext);

  const handleGetStarted = async () => {
    if (tutorialContext) {
      await tutorialContext.markCompleted('getting_started');
    }
    navigate('/stores');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Getting Started</Typography>
        </Box>
      </Box>

      <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
        Welcome to Grocery Price Checker! Here's a quick overview of how to get the most out of the app.
      </Typography>

      <Stepper orientation="vertical" activeStep={-1}>
        {steps.map((step) => (
          <Step key={step.label} active expanded>
            <StepLabel
              icon={step.icon}
              sx={{
                '& .MuiStepLabel-label': {
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                },
              }}
            >
              {step.label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleGetStarted}
          sx={{ minWidth: 200 }}
        >
          Get Started
        </Button>
      </Box>
    </Container>
  );
};

export default GettingStarted;
